<template>
  <b-modal
    id="audioTranscriptionModal"
    v-model="show"
    title="ผลการแปลงเสียงเป็นข้อความ"
    size="lg"
    :hide-header-close="false"
    :no-close-on-backdrop="false"
    :no-close-on-esc="false"
    @hidden="onModalHidden"
    hide-footer
  >
    <div class="row">
      <!-- Main transcription area -->
      <div :class="showHistory ? 'col-md-8' : 'col-md-12'">
        <div class="form-group">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label for="transcriptionText" class="font-weight-bold mb-0">
              ข้อความที่แปลงได้:
              <span v-if="isTranscribing" class="streaming-indicator ml-2">
                <i class="fas fa-circle text-success streaming-dot"></i>
                <small class="text-success">กำลังรับข้อความ...</small>
              </span>
            </label>
            <div class="text-stats d-flex align-items-center gap-3">
              <small class="text-muted">
                <i class="fas fa-file-alt"></i>
                {{ wordCount }} คำ
              </small>
              <small class="text-muted">
                <i class="fas fa-keyboard"></i>
                {{ characterCount }} ตัวอักษร
              </small>
              <div v-if="hasUnsavedChanges" class="unsaved-indicator">
                <i class="fas fa-circle text-warning"></i>
                <small class="text-warning">มีการแก้ไข</small>
              </div>
            </div>
          </div>
          <div class="textarea-container position-relative">
            <b-form-textarea
              id="transcriptionText"
              v-model="currentTranscription.text"
              @input="onTextChange"
              rows="12"
              max-rows="20"
              :placeholder="
                isTranscribing
                  ? 'ข้อความที่แปลงจากเสียงจะปรากฏที่นี่...'
                  : currentTranscription.text
                  ? 'ข้อความที่แปลงจากเสียงจะปรากฏที่นี่...'
                  : 'ไม่มีการแปลงเสียงปัจจุบัน - เลือกจากประวัติด้านขวาหรือบันทึกเสียงใหม่'
              "
              class="transcription-textarea enhanced"
              :readonly="isTranscribing"
            ></b-form-textarea>
            
            <!-- Transcription processing overlay - only show when loading -->
            <div v-if="shouldShowOverlay" class="transcription-overlay">
              <div class="overlay-content">
                <b-spinner variant="primary" class="mb-3" style="width: 3rem; height: 3rem;"></b-spinner>
                <div class="processing-text">กำลังแปลงเสียงเป็นข้อความ...</div>
                <div class="processing-subtitle">โปรดรอสักครู่ ข้อความจะปรากฏที่นี่เมื่อการประมวลผลเสร็จสิ้น</div>
                <div class="processing-status mt-2">
                  <small class="text-muted">
                    <i class="fas fa-microphone"></i> 
                    ไฟล์เสียงกำลังถูกประมวลผลบนเซิร์ฟเวอร์
                  </small>
                </div>
              </div>
            </div>
          </div>
          <small class="text-muted mt-2">
            <i class="fas fa-info-circle"></i>
            คุณสามารถแก้ไขข้อความได้ก่อนใช้งาน
          </small>
        </div>

        <!-- Main transcription actions -->
        <div class="transcription-actions mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <div class="left-actions">
              <b-button
                variant="primary"
                @click="copyToClipboard"
                :disabled="!currentTranscription.text.trim() || isTranscribing"
                class="me-2"
                :class="{ 'btn-success': copySuccess }"
              >
                <i :class="copySuccess ? 'fas fa-check' : 'fas fa-copy'"></i> 
                {{ copySuccess ? 'คัดลอกแล้ว' : 'คัดลอก' }}
              </b-button>
              
              <b-button
                v-if="!showHistory && transcriptionHistory.length > 0"
                variant="outline-info"
                size="sm"
                @click="toggleHistory"
              >
                <i class="fas fa-history"></i> แสดงประวัติ
              </b-button>
            </div>
            
            <b-button
              variant="outline-danger"
              size="sm"
              @click="clearTranscription"
              :disabled="!currentTranscription.text.trim()"
              class="clear-button"
            >
              <i class="fas fa-trash"></i> ล้างข้อความ
            </b-button>
          </div>
        </div>
      </div>

      <!-- History sidebar -->
      <div v-if="showHistory" class="col-md-4">
        <div class="history-panel">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="font-weight-bold mb-0">
              <i class="fas fa-history"></i> ประวัติการแปลงเสียง
            </h6>
            <b-button 
              variant="outline-secondary" 
              size="sm" 
              @click="toggleHistory"
              class="history-toggle-btn"
            >
              <i class="fas fa-chevron-left"></i>
            </b-button>
          </div>

          <div class="history-search mb-3">
            <b-input-group size="sm">
              <b-form-input
                v-model="historySearch"
                placeholder="ค้นหาในประวัติ..."
                class="history-search-input"
              ></b-form-input>
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  @click="historySearch = ''"
                  title="ล้างการค้นหา"
                >
                  <i class="fas fa-eraser"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <div class="history-list">
            <div
              v-for="item in filteredHistory"
              :key="item.id"
              class="history-item"
              @click="loadFromHistory(item)"
            >
              <div class="history-header">
                <small class="text-muted">
                  {{ formatDate(item.timestamp) }}
                  <span v-if="item.lastModified && item.lastModified !== item.timestamp" class="modified-indicator">
                    <i class="fas fa-edit"></i>
                  </span>
                </small>
                <div class="history-actions">
                  <b-button
                    variant="link"
                    size="sm"
                    class="p-0 delete-history-btn"
                    @click.stop="removeFromHistory(item.id)"
                    title="ลบรายการนี้"
                  >
                    <i class="fas fa-trash-alt text-muted"></i>
                  </b-button>
                </div>
              </div>
              
              <!-- History text preview -->
              <div class="history-preview">
                {{ item.text.substring(0, 100) }}
                <span v-if="item.text.length > 100">...</span>
              </div>
            </div>

            <div
              v-if="filteredHistory.length === 0"
              class="text-center text-muted p-3"
            >
              <i class="fas fa-inbox"></i><br />
              {{
                historySearch
                  ? "ไม่พบรายการที่ค้นหา"
                  : "ยังไม่มีประวัติการแปลงเสียง"
              }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal footer -->
    <template #modal-footer="{ close }">
      <div class="d-flex justify-content-between w-100">
        <div></div>
        <div class="d-flex gap-2">
          <b-button 
            v-if="hasUnsavedChanges || showReviewStep"
            variant="success" 
            @click="saveAndClose()"
          >
            <i class="fas fa-save"></i> บันทึกและปิด
          </b-button>
          <b-button 
            v-if="hasUnsavedChanges"
            variant="outline-secondary" 
            @click="closeWithoutSaving()"
          >
            ปิดโดยไม่บันทึก
          </b-button>
          <b-button 
            v-else
            variant="secondary" 
            @click="close()"
          >
            ปิด
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
// Modal constants
const MODAL_STATES = {
  CLOSED: 'closed',
  LOADING: 'loading',
  STREAMING: 'streaming',
  COMPLETE: 'complete',
  ERROR: 'error'
};

const STORAGE_KEYS = {
  TRANSCRIPTION_HISTORY: 'transcriptionHistory'
};

const MAX_HISTORY_ITEMS = 20;

export default {
  name: "AudioTranscriptionModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    transcriptionText: {
      type: String,
      default: "",
    },
    isTranscribing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Modal state
      show: false,
      showHistory: false,
      manuallyClosing: false,
      
      // Search and filtering
      historySearch: "",
      
      // Current transcription
      currentTranscription: {
        id: null,
        text: "",
        timestamp: null,
      },
      originalText: "",
      hasUnsavedChanges: false,
      
      // History management
      transcriptionHistory: [],
      
      // UI feedback
      copySuccess: false,
      saveSuccess: false,
      
      // Settings
      autoSaveEnabled: true,
      showReviewStep: false,
      
      // Auto-save functionality
      autoSaveDebounceTimer: null,
      autoSaveDelay: 2000, // 2 seconds delay after last edit
    };
  },
  computed: {
    filteredHistory() {
      if (!this.historySearch.trim()) {
        return this.transcriptionHistory;
      }

      const search = this.historySearch.toLowerCase();
      return this.transcriptionHistory.filter(item =>
        item.text.toLowerCase().includes(search)
      );
    },
    wordCount() {
      if (!this.currentTranscription.text) return 0;
      return this.currentTranscription.text.trim().split(/\s+/).filter(word => word.length > 0).length;
    },
    characterCount() {
      if (!this.currentTranscription.text) return 0;
      return this.currentTranscription.text.length;
    },
    
    // Modal state helpers
    isStreamingMode() {
      return this.isTranscribing && this.currentTranscription.text.trim();
    },
    
    isLoadingMode() {
      return this.isTranscribing && !this.currentTranscription.text.trim();
    },
    
    hasContent() {
      return this.currentTranscription.text.trim().length > 0;
    },
    
    shouldShowOverlay() {
      return this.isLoadingMode;
    },
  },
  watch: {
    value(newVal) {
      this.show = newVal;
    },
    show(newVal) {
      this.$emit("input", newVal);

      // Auto-show history sidebar when modal opens without current transcription
      if (
        newVal &&
        !this.currentTranscription.text.trim() &&
        this.transcriptionHistory.length > 0
      ) {
        this.showHistory = true;
      }
    },
    isTranscribing(newVal, oldVal) {
      // When transcription starts, reset the current transcription state
      // This ensures we create a new entry instead of overwriting loaded history
      if (newVal && !oldVal) {
        // Clear ID to force creation of new entry
        this.currentTranscription.id = null;
        this.currentTranscription.timestamp = null;
        this.hasUnsavedChanges = false;
      }
      
      // When transcription completes, save to history
      if (!newVal && oldVal && this.currentTranscription.text.trim()) {
        this.createNewTranscriptionEntry(this.currentTranscription.text);
        if (this.autoSaveEnabled) {
          this.saveToHistory();
        }
      }
    },
    transcriptionText(newVal) {
      try {
        // Always update the text during transcription streaming
        if (this.isTranscribing || (newVal && newVal !== this.currentTranscription.text)) {
          this.currentTranscription.text = newVal || "";
          
          // Only update metadata when transcription starts or completes
          if (!this.isTranscribing) {
            // Always create new entry for completed transcriptions
            this.createNewTranscriptionEntry(newVal);
            // Auto-save after transcription completes
            if (this.autoSaveEnabled && newVal && newVal.trim()) {
              this.saveToHistory();
            }
          }
        }
      } catch (error) {
        console.warn("Error updating transcription text:", error);
        this.emitMessage({
          type: "warning",
          text: "มีปัญหาในการอัปเดตข้อความ"
        });
      }
    },
  },
  mounted() {
    this.loadHistory();
    this.setupKeyboardShortcuts();
  },
  beforeDestroy() {
    this.removeKeyboardShortcuts();
    // Clean up auto-save timer
    if (this.autoSaveDebounceTimer) {
      clearTimeout(this.autoSaveDebounceTimer);
    }
  },
  methods: {
    onModalHidden() {
      // Only auto-save if auto-save is enabled and not manually closed
      if (this.autoSaveEnabled && this.currentTranscription.text.trim() && !this.manuallyClosing) {
        this.saveToHistory();
      }
      this.manuallyClosing = false;
    },

    saveAndClose() {
      if (this.currentTranscription.text.trim()) {
        this.saveToHistory();
        this.saveSuccess = true;
        this.$emit("message", {
          type: "success",
          text: "บันทึกการแปลงเสียงเรียบร้อยแล้ว",
        });
        
        setTimeout(() => {
          this.saveSuccess = false;
        }, 1000);
      }
      this.hasUnsavedChanges = false;
      this.manuallyClosing = true;
      this.$emit("input", false);
    },

    closeWithoutSaving() {
      if (this.hasUnsavedChanges) {
        this.$bvModal
          .msgBoxConfirm(
            "คุณมีการแก้ไขที่ยังไม่ได้บันทึก ต้องการปิดโดยไม่บันทึกหรือไม่?",
            {
              title: "ยืนยันการปิดโดยไม่บันทึก",
              okTitle: "ปิดโดยไม่บันทึก",
              cancelTitle: "ยกเลิก",
              okVariant: "danger",
            }
          )
          .then(value => {
            if (value) {
              this.hasUnsavedChanges = false;
              this.manuallyClosing = true;
              this.$emit("input", false);
            }
          });
      } else {
        this.manuallyClosing = true;
        this.$emit("input", false);
      }
    },

    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.currentTranscription.text);
        this.copySuccess = true;
        this.$emit("message", {
          type: "success",
          text: "คัดลอกข้อความเรียบร้อยแล้ว",
        });
        
        // Reset copy success indicator after 2 seconds
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = this.currentTranscription.text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        this.copySuccess = true;
        this.$emit("message", {
          type: "success",
          text: "คัดลอกข้อความเรียบร้อยแล้ว",
        });
        
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      }
    },


    clearTranscription() {
      this.$bvModal
        .msgBoxConfirm("คุณต้องการล้างข้อความนี้หรือไม่?", {
          title: "ยืนยันการล้างข้อความ",
          okTitle: "ล้าง",
          cancelTitle: "ยกเลิก",
          okVariant: "danger",
        })
        .then(value => {
          if (value) {
            this.currentTranscription.text = "";
            this.currentTranscription.timestamp = null;
            this.currentTranscription.id = null;
          }
        });
    },

    toggleHistory() {
      this.showHistory = !this.showHistory;
    },

    loadFromHistory(item) {
      this.currentTranscription = { ...item };
      this.originalText = item.text;
      this.hasUnsavedChanges = false;
    },

    removeFromHistory(id) {
      this.transcriptionHistory = this.transcriptionHistory.filter(
        item => item.id !== id
      );
      this.saveHistory();
    },

    saveToHistory() {
      if (!this.currentTranscription.text.trim()) return;

      // Ensure ID and timestamp are set for new transcriptions
      if (!this.currentTranscription.id) {
        this.currentTranscription.id = Date.now();
        this.currentTranscription.timestamp = new Date();
      }

      // Check if this transcription already exists in history
      const existingIndex = this.transcriptionHistory.findIndex(
        item => item.id === this.currentTranscription.id
      );

      if (existingIndex >= 0) {
        // Update existing entry using Vue.set for proper reactivity
        this.$set(this.transcriptionHistory, existingIndex, {
          ...this.currentTranscription,
        });
      } else {
        // Create new array to ensure Vue reactivity
        const newEntry = { ...this.currentTranscription };
        this.transcriptionHistory = [newEntry, ...this.transcriptionHistory];

        // Keep only last MAX_HISTORY_ITEMS items
        if (this.transcriptionHistory.length > MAX_HISTORY_ITEMS) {
          this.transcriptionHistory = this.transcriptionHistory.slice(0, MAX_HISTORY_ITEMS);
        }
      }

      this.saveHistory();
    },

    saveHistory() {
      try {
        localStorage.setItem(
          STORAGE_KEYS.TRANSCRIPTION_HISTORY,
          JSON.stringify(this.transcriptionHistory)
        );
        
        // Emit custom event to notify other components
        window.dispatchEvent(new CustomEvent('transcriptionHistoryUpdated', {
          detail: this.transcriptionHistory
        }));
      } catch (error) {
        console.warn("Unable to save transcription history:", error);
        this.emitMessage({
          type: "warning",
          text: "ไม่สามารถบันทึกประวัติได้ พื้นที่จัดเก็บข้อมูลอาจเต็ม"
        });
      }
    },

    loadHistory() {
      try {
        const saved = localStorage.getItem(STORAGE_KEYS.TRANSCRIPTION_HISTORY);
        if (saved) {
          this.transcriptionHistory = JSON.parse(saved);
        }
      } catch (error) {
        console.warn("Unable to load transcription history:", error);
        this.transcriptionHistory = [];
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return "";

      const date = new Date(timestamp);
      
      return date.toLocaleDateString("th-TH", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    setupKeyboardShortcuts() {
      // Keyboard shortcuts removed as requested
    },

    removeKeyboardShortcuts() {
      // Keyboard shortcuts removed as requested
    },

    onTextChange() {
      this.hasUnsavedChanges = this.currentTranscription.text !== this.originalText;
      
      // Auto-save after edit with debounce
      if (this.autoSaveEnabled && this.currentTranscription.text.trim()) {
        // Clear existing timer
        if (this.autoSaveDebounceTimer) {
          clearTimeout(this.autoSaveDebounceTimer);
        }
        
        // Set new timer
        this.autoSaveDebounceTimer = setTimeout(() => {
          this.saveToHistory();
          this.hasUnsavedChanges = false;
          this.originalText = this.currentTranscription.text;
          this.emitMessage({
            type: "info",
            text: "บันทึกอัตโนมัติเรียบร้อยแล้ว"
          });
        }, this.autoSaveDelay);
      }
    },



    // Helper Methods
    createNewTranscriptionEntry(text) {
      // Always create a new transcription entry for completed transcriptions
      this.currentTranscription.timestamp = new Date();
      this.currentTranscription.id = Date.now();
      this.originalText = text || "";
      this.hasUnsavedChanges = false;
      
      // Note: saveToHistory() is now called in the transcriptionText watcher
      // to avoid double-saving
    },

    emitMessage(message) {
      this.$emit("message", message);
    },
  },
};
</script>

<style scoped>
.transcription-textarea {
  font-family: var(--ci-font-family, "Sarabun", sans-serif);
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
}

.transcription-textarea.enhanced {
  font-size: 15px;
  line-height: 1.6;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: border-color 0.3s ease;
}

.transcription-textarea.enhanced:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.transcription-textarea.enhanced[readonly] {
  background-color: #f8f9fa;
  border-color: #28a745;
  box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.15);
  cursor: default;
}

.text-stats {
  font-size: 12px;
}

.text-stats i {
  margin-right: 4px;
}

.unsaved-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.unsaved-indicator i {
  font-size: 8px;
  animation: pulse-warning 2s infinite;
}

@keyframes pulse-warning {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}


.textarea-container {
  position: relative;
}

.transcription-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 10;
}

.overlay-content {
  text-align: center;
  padding: 20px;
}

/* Transcription Processing Overlay */
.processing-text {
  font-size: 18px;
  font-weight: 600;
  color: #007bff;
  margin-bottom: 8px;
}

.processing-subtitle {
  font-size: 14px;
  color: #6c757d;
  line-height: 1.4;
  margin-bottom: 4px;
}

.processing-status {
  padding: 8px 12px;
  background: rgba(0, 123, 255, 0.1);
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

/* Streaming Indicators */
.streaming-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.streaming-dot {
  font-size: 8px;
  animation: transcription-streaming-pulse 1.5s infinite;
}

@keyframes transcription-streaming-pulse {
  0%, 100% { 
    opacity: 1; 
    transform: scale(1);
  }
  50% { 
    opacity: 0.5; 
    transform: scale(1.2);
  }
}

.btn-success.btn-primary {
  background-color: #28a745;
  border-color: #28a745;
  animation: success-pulse 0.3s ease;
}

@keyframes success-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.transcription-actions .left-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.clear-button {
  opacity: 0.8;
}

.clear-button:hover {
  opacity: 1;
}

.history-toggle-btn {
  min-width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-history-btn {
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.delete-history-btn:hover {
  opacity: 1;
}

.delete-history-btn:hover i {
  color: #dc3545 !important;
}

.history-panel {
  border-left: 1px solid #dee2e6;
  padding-left: 1rem;
  height: 500px;
  overflow-y: auto;
}

.history-search-input {
  font-size: 12px;
}

.history-search .input-group {
  align-items: stretch;
}

.history-search .input-group .form-control,
.history-search .input-group .btn {
  height: 31px;
  line-height: 1.5;
  border: 1px solid #ced4da;
  vertical-align: top;
}

.history-search .input-group .form-control {
  border-right: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.history-search .input-group-append .btn {
  border-left: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: -1px;
}

.history-list {
  max-height: 400px;
  overflow-y: auto;
}

.history-item {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.history-item:hover {
  background-color: #f8f9fa;
  border-color: #007bff;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}


.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.history-preview {
  font-size: 12px;
  color: #6c757d;
  line-height: 1.4;
}

.history-actions {
  display: flex;
  align-items: center;
}

.delete-history-btn {
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.history-item:hover .delete-history-btn {
  opacity: 1;
}

.delete-history-btn:hover i {
  color: #dc3545 !important;
}

.modified-indicator {
  margin-left: 4px;
  color: #28a745;
  font-size: 10px;
}


.keyboard-shortcuts {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.keyboard-shortcuts kbd {
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.25);
  color: #495057;
  font-size: 0.75rem;
  padding: 0.1875rem 0.375rem;
}

.gap-2 > * {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

.gap-2 > *:last-child {
  margin-right: 0;
}

@media (max-width: 768px) {
  .history-panel {
    border-left: none;
    border-top: 1px solid #dee2e6;
    padding-left: 0;
    padding-top: 1rem;
    margin-top: 1rem;
  }
}
</style>